import { decFmt, fmtBrl, fmtInt } from './shared/formatters'
import { buildSharedMonths, buildSummaryPeriods, getCurrentMonthKey } from './shared/periods'

import { calculateAggregateMetrics } from './aggregators/salesMetrics'
import { calculateStockAggregate } from './aggregators/stockAggregate'
import { calculateCmvByCategory } from './aggregators/cmvByCategory'
import { buildHybridEntries } from './aggregators/hybridEntries'

import { buildSalesRows } from './rowGroups/salesRows'
import { buildStockRows } from './rowGroups/stockRows'

export { buildRawMonthlySeries } from './rawMonthlySeries'

export const buildUnifiedRowGroups = ({
  planoData, realizadoData, realizadoLastYearData, estoquePecasData,
  estoqueReaisData, brandData, visualizacao, usePriceCostAsCMV = true }) => {
  const plano = planoData || [];
  const realizadoBase = realizadoData || [];
  const estoquePecas = estoquePecasData || [];
  const estoqueReais = estoqueReaisData || [];
  const realizadoLastYear = realizadoLastYearData || [];

  const monthsWithRealizado = new Set(
    realizadoBase.map(d => `${Number(d.year)}-${Number(d.month)}`)
  );
  const editedOverrides = plano.filter(
    d => d.editedOverride && monthsWithRealizado.has(`${Number(d.year)}-${Number(d.month)}`)
  );
  const realizado = editedOverrides.length > 0
    ? realizadoBase.concat(editedOverrides)
    : realizadoBase;

  const hasAnyData = [plano, realizado, estoquePecas, estoqueReais].some(arr => arr.length > 0);
  if (!hasAnyData) return null;

  const { monthKeys, months } = buildSharedMonths([plano, realizado, estoquePecas, estoqueReais]);
  const summaryPeriods = buildSummaryPeriods(monthKeys);

  const planoAgg = calculateAggregateMetrics({ data: plano, monthKeys });
  const realizadoAgg = calculateAggregateMetrics({ data: realizado, monthKeys });

  const hybridEntries = buildHybridEntries({ plano, realizado, currentMonthKey: getCurrentMonthKey() });
  const hybridAgg = calculateAggregateMetrics({ data: hybridEntries, monthKeys });

  const pecasAgg = calculateStockAggregate({
    data: estoquePecas, monthKeys,
    sumFields: [
      { field: 'initialInventoryUnits', format: fmtInt },
      { field: 'endingInventoryUnits', format: fmtInt },
      { field: 'openToBuyUnits', format: fmtInt },
      { field: 'grossOpenToBuyUnits', format: fmtInt },
      { field: 'inboundInventoryUnits', format: fmtInt },
      { field: 'minimumInventoryUnits', format: fmtInt },
      { field: 'idealInventoryUnits', format: fmtInt },
    ],
    avgFields: [
      { field: 'actualInventoryCoverage', format: (v) => decFmt.format(v) + "x" },
      { field: 'consolidatedCoverage', format: (v) => decFmt.format(v) + "x" },
      { field: 'plannedInventoryCoverage', format: (v) => decFmt.format(v) + "x" },
    ],
  });

  const reaisAgg = calculateStockAggregate({
    data: estoqueReais, monthKeys,
    sumFields: [
      { field: 'initialInventoryValue', format: fmtBrl },
      { field: 'realPortfolioValue', format: fmtBrl },
      { field: 'finalInventoryValue', format: fmtBrl },
      { field: 'costEntriesValue', format: fmtBrl },
      { field: 'minimumInventoryValue', format: fmtBrl },
      { field: 'idealInventoryValue', format: fmtBrl },
      { field: 'purchaseValueFromCategory', format: fmtBrl },
    ],
    avgFields: [],
  });

  const cmvAgg = calculateCmvByCategory({
    salesData: hybridEntries,
    estoquePecasData: estoquePecas,
    estoqueReaisData: estoqueReais,
    monthKeys,
  });

  const { variacoes, estoqueReais: showInventoryValueAtRetail, forecast, custosPreco } = visualizacao || {};

  const { faturamentoRows, vendaPecasRows, precoRows, custoRows } = buildSalesRows({
    monthKeys,
    summaryPeriods,
    planoAgg,
    realizadoAgg,
    hybridAgg,
    pecasAgg,
    reaisAgg,
    cmvAgg,
    realizadoLastYear,
    variacoes,
    forecast,
    custosPreco,
    usePriceCostAsCMV,
  });

  const estoqueRows = buildStockRows({
    monthKeys,
    summaryPeriods,
    plano,
    realizado,
    estoquePecas,
    brandData,
    planoAgg,
    realizadoAgg,
    hybridAgg,
    pecasAgg,
    reaisAgg,
    plannedSalesRaw: vendaPecasRows[0].rawValues,
    showInventoryValueAtRetail,
  });

  return {
    monthKeys,
    months,
    summaryPeriods,
    groups: [
      { grupo: "Faturamento", rows: faturamentoRows },
      { grupo: "Venda Peças", rows: vendaPecasRows },
      { grupo: "Preço", rows: precoRows },
      { grupo: "Estoque", rows: estoqueRows },
      { grupo: "Custo", rows: custoRows },
    ],
  };
};